import { _ as useTheme_v2, d as createGlobalScopedContext, f as globalScope, r as isRecord } from "./useLayer-BAVfmbNN.js";
import { c } from "react/compiler-runtime";
import { useContext } from "react";
import { css, styled } from "styled-components";
import { jsx, jsxs } from "react/jsx-runtime";
import { createPortal } from "react-dom";
const BoundaryElementContext = createGlobalScopedContext("@sanity/ui/context/boundaryElement", null), DEFAULT_VALUE = {
	version: 0,
	element: null
};
/**
* @public
*/
function useBoundaryElement() {
	let value = useContext(BoundaryElementContext);
	if (value && (!isRecord(value) || value.version !== 0)) throw Error("useBoundaryElement(): the context value is not compatible");
	return value || DEFAULT_VALUE;
}
const key = "@sanity/ui/context/portal", elementKey = Symbol.for(`${key}/element`);
globalScope[elementKey] = null;
const PortalContext = createGlobalScopedContext(key, {
	version: 0,
	boundaryElement: null,
	get element() {
		return typeof document > "u" ? null : globalScope[elementKey] ? globalScope[elementKey] : (globalScope[elementKey] = document.createElement("div"), globalScope[elementKey].setAttribute("data-portal", ""), document.body.appendChild(globalScope[elementKey]), globalScope[elementKey]);
	}
});
/**
* @public
*/
function usePortal() {
	let value = useContext(PortalContext);
	if (!value) throw Error("usePortal(): missing context value");
	if (!isRecord(value) || value.version !== 0) throw Error("usePortal(): the context value is not compatible");
	return value;
}
/**
* @public
*/
function Portal(props) {
	let $ = c(3), { children, __unstable_name: name } = props, portal = usePortal(), portalElement = (name ? portal.elements && portal.elements[name] : portal.element) || portal.elements?.default;
	if (!portalElement) return null;
	let t0;
	return $[0] !== children || $[1] !== portalElement ? (t0 = createPortal(children, portalElement), $[0] = children, $[1] = portalElement, $[2] = t0) : t0 = $[2], t0;
}
function moveTowardsLength(movingPoint, targetPoint, amount) {
	let width = targetPoint.x - movingPoint.x, height = targetPoint.y - movingPoint.y, distance = Math.sqrt(width * width + height * height);
	return moveTowardsFractional(movingPoint, targetPoint, Math.min(1, amount / distance));
}
function moveTowardsFractional(movingPoint, targetPoint, fraction) {
	return {
		x: movingPoint.x + (targetPoint.x - movingPoint.x) * fraction,
		y: movingPoint.y + (targetPoint.y - movingPoint.y) * fraction
	};
}
function getRoundedCommands(points) {
	let len = points.length, cmds = [];
	for (let i = 0; i < len; i += 1) {
		let point = points[i], prevPoint = points[i - 1], nextPoint = points[i + 1];
		if (prevPoint && point.radius) {
			let curveStart = moveTowardsLength(point, prevPoint, point.radius), curveEnd = moveTowardsLength(point, nextPoint, point.radius), startControl = moveTowardsFractional(curveStart, point, .5), endControl = moveTowardsFractional(point, curveEnd, .5);
			cmds.push({
				type: "point",
				...curveStart
			}), cmds.push({
				type: "curve",
				curveEnd,
				startControl,
				endControl
			});
		} else cmds.push({
			type: "point",
			...point
		});
	}
	return cmds;
}
function compileCommands(cmds) {
	return cmds.map((n, idx) => n.type === "point" ? `${idx === 0 ? "M" : "L"} ${n.x} ${n.y}` : n.type === "curve" ? `C ${n.startControl.x} ${n.startControl.y} ${n.endControl.x} ${n.endControl.y} ${n.curveEnd.x} ${n.curveEnd.y}` : "").join(" ");
}
const StyledArrow = /* @__PURE__ */ styled.div.withConfig({
	displayName: "StyledArrow",
	componentId: "sc-e0ij84-0"
})(({ $w: w }) => css`position:absolute;width:${w}px;height:${w}px;:empty + &{display:none;}& > svg{display:block;line-height:0;transform-origin:${w / 2}px ${w / 2}px;}[data-placement^='top'] > &{bottom:-${w}px;& > svg{transform:rotate(0);}}[data-placement^='right'] > &{left:-${w}px;& > svg{transform:rotate(90deg);}}[data-placement^='left'] > &{right:-${w}px;& > svg{transform:rotate(-90deg);}}[data-placement^='bottom'] > &{top:-${w}px;& > svg{transform:rotate(180deg);}}`), StrokePath = styled.path.withConfig({
	displayName: "StrokePath",
	componentId: "sc-e0ij84-1"
})`stroke:var(--card-shadow-outline-color);`, ShapePath = styled.path.withConfig({
	displayName: "ShapePath",
	componentId: "sc-e0ij84-2"
})`fill:var(--card-bg-color);`;
/** @internal */
function Arrow(props) {
	let $ = c(30), h, ref, restProps, t0, w;
	$[0] === props ? (h = $[1], ref = $[2], restProps = $[3], t0 = $[4], w = $[5]) : ({width: w, height: h, radius: t0, ref, ...restProps} = props, $[0] = props, $[1] = h, $[2] = ref, $[3] = restProps, $[4] = t0, $[5] = w);
	let radius = t0 === void 0 ? 0 : t0, { card } = useTheme_v2(), strokeWidth = card.shadow.outline, center = w / 2, t1;
	$[6] !== center || $[7] !== h || $[8] !== radius || $[9] !== w ? (t1 = compileCommands(getRoundedCommands([
		{
			x: 0,
			y: 0
		},
		{
			x: radius,
			y: 0,
			radius
		},
		{
			x: center,
			y: h - 1,
			radius
		},
		{
			x: w - radius,
			y: 0,
			radius
		},
		{
			x: w,
			y: 0
		}
	])), $[6] = center, $[7] = h, $[8] = radius, $[9] = w, $[10] = t1) : t1 = $[10];
	let path = t1, strokePath = `${path}`, fillPath = `${path} M ${w} -1 M 0 -1 Z`, t2 = `0 0 ${w} ${w}`, t3;
	$[11] !== strokeWidth || $[12] !== w ? (t3 = /* @__PURE__ */ jsx("mask", {
		id: "stroke-mask",
		children: /* @__PURE__ */ jsx("rect", {
			x: 0,
			y: strokeWidth,
			width: w,
			height: w,
			fill: "white"
		})
	}), $[11] = strokeWidth, $[12] = w, $[13] = t3) : t3 = $[13];
	let t4 = strokeWidth * 2, t5;
	$[14] !== strokePath || $[15] !== t4 ? (t5 = /* @__PURE__ */ jsx(StrokePath, {
		d: strokePath,
		mask: "url(#stroke-mask)",
		strokeWidth: t4
	}), $[14] = strokePath, $[15] = t4, $[16] = t5) : t5 = $[16];
	let t6;
	$[17] === fillPath ? t6 = $[18] : (t6 = /* @__PURE__ */ jsx(ShapePath, { d: fillPath }), $[17] = fillPath, $[18] = t6);
	let t7;
	$[19] !== t2 || $[20] !== t3 || $[21] !== t5 || $[22] !== t6 || $[23] !== w ? (t7 = /* @__PURE__ */ jsxs("svg", {
		width: w,
		height: w,
		viewBox: t2,
		children: [
			t3,
			t5,
			t6
		]
	}), $[19] = t2, $[20] = t3, $[21] = t5, $[22] = t6, $[23] = w, $[24] = t7) : t7 = $[24];
	let t8;
	return $[25] !== ref || $[26] !== restProps || $[27] !== t7 || $[28] !== w ? (t8 = /* @__PURE__ */ jsx(StyledArrow, {
		...restProps,
		$w: w,
		ref,
		children: t7
	}), $[25] = ref, $[26] = restProps, $[27] = t7, $[28] = w, $[29] = t8) : t8 = $[29], t8;
}
export { useBoundaryElement as a, PortalContext as i, Portal as n, BoundaryElementContext as o, usePortal as r, Arrow as t };

//# sourceMappingURL=arrow-CHE8-_G7.js.map